import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import useContent from '../hooks/useContent';
import BlockEditor from '../components/BlockEditor';
import MediaPicker from '../components/MediaPicker';

const emptyContent = {
  title: '',
  slug: '',
  contentType: 'post',
  status: 'draft',
  body: { blocks: [] },
  excerpt: '',
  featuredImage: null,
};

function slugify(value) {
  return value.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export default function ContentEdit() {
  const { contentId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { createContent, getContent, updateContent } = useContent();
  const initialType = searchParams.get('type') === 'page' ? 'page' : 'post';

  const [form, setForm] = useState({ ...emptyContent, contentType: initialType });
  const [versionHistory, setVersionHistory] = useState([]);
  const [loading, setLoading] = useState(contentId !== 'new');
  const [saving, setSaving] = useState(false);
  const [saveLabel, setSaveLabel] = useState('');
  const [error, setError] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);

  const isNew = useMemo(() => !contentId || contentId === 'new', [contentId]);

  const handleTitleChange = (value) => {
    setForm((current) => ({
      ...current,
      title: value,
      // Auto-generate slug from title only while user hasn't manually edited it
      slug: isNew && !current._slugEdited ? slugify(value) : current.slug,
    }));
  };

  useEffect(() => {
    if (isNew) {
      setForm({ ...emptyContent, contentType: initialType });
      setVersionHistory([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getContent(contentId)
      .then((res) => {
        if (!cancelled) {
          setForm({ ...res.item, _slugEdited: true });
          setVersionHistory(res.versionHistory || []);
        }
      })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [contentId, getContent, isNew, initialType]);

  const save = async (status) => {
    setSaving(true);
    setSaveLabel(status === 'published' ? 'Veröffentlichen…' : 'Speichern…');
    setError('');
    const payload = { ...form, status };
    if (!form.slug && form.title) payload.slug = slugify(form.title);
    try {
      if (isNew) {
        const res = await createContent(payload);
        if (status === 'published') {
          await updateContent(res.item.contentId, { ...res.item, ...payload, status: 'published' });
        }
        navigate(`/content/${res.item.contentId}`);
      } else {
        const updateRes = await updateContent(contentId, payload);
        if (updateRes && updateRes.item) {
          setForm({ ...updateRes.item, _slugEdited: true });
        }
        // Graceful 1000ms delay for read-after-write propagation with locked UI
        await new Promise((r) => setTimeout(r, 1000));
        const refreshed = await getContent(contentId, true);
        setForm({ ...refreshed.item, _slugEdited: true });
        setVersionHistory(refreshed.versionHistory || []);
        setSaveLabel('Gespeichert ✓');
        setTimeout(() => setSaveLabel(''), 2500);
      }
    } catch (err) {
      setError(err.message);
      setSaveLabel('');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* ── Sticky top toolbar ── */}
      <div className="editor-topbar">
        <div className="row" style={{ gap: '0.75rem' }}>
          <button type="button" className="button ghost small" onClick={() => navigate('/content')}>
            ← Zurück
          </button>
          <span className={`save-indicator${saving ? ' saving' : ''}`}>
            {saving ? (saveLabel || 'Speichert… (1s Sperre)') : (saveLabel || (isNew ? 'Neuer Entwurf' : 'Alle Änderungen gespeichert'))}
          </span>
        </div>
        <div className="button-row">
          {!isNew && (
            <button
              type="button"
              className="button secondary"
              onClick={() => navigate(`/content/${contentId}/preview`)}
              disabled={saving}
            >
              Live-Vorschau 👁️
            </button>
          )}
          <button className="button ghost" onClick={() => save('draft')} disabled={saving}>
            Entwurf speichern
          </button>
          <button className="button primary" onClick={() => save('published')} disabled={saving}>
            {form.status === 'published' ? 'Aktualisieren' : 'Veröffentlichen'}
          </button>
        </div>
      </div>

      {saving ? (
        <div className="alert info" style={{ marginTop: '0.75rem', background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe' }}>
          🔒 <strong>Eingaben gesperrt:</strong> Daten werden gespeichert und mit DynamoDB synchronisiert (1 Sekunde Schreibschutz)…
        </div>
      ) : null}

      {error ? <div className="alert error" style={{ marginTop: '0.75rem' }}>{error}</div> : null}

      {loading ? <p style={{ marginTop: '2rem', color: '#6b7280' }}>Lade Inhalt…</p> : (
        <div className="editor-layout" style={{ opacity: saving ? 0.7 : 1, pointerEvents: saving ? 'none' : 'auto' }}>

          {/* ── Left: writing area ── */}
          <div className="editor-main">
            <input
              className="editor-title"
              placeholder="Titel hinzufügen…"
              value={form.title}
              onChange={(e) => handleTitleChange(e.target.value)}
              disabled={saving}
            />
            <BlockEditor
              value={form.body}
              onChange={(body) => setForm((c) => ({ ...c, body }))}
              readOnly={saving}
            />
          </div>

          {/* ── Right: sidebar panel ── */}
          <aside className="editor-sidebar">

            {/* Publish card */}
            <div className="card">
              <h4>Veröffentlichen</h4>
              <div className="publish-status">
                <span className={`status-dot ${form.status}`} />
                <span style={{ textTransform: 'capitalize' }}>
                  {form.status === 'draft' ? 'Entwurf' : form.status === 'published' ? 'Veröffentlicht' : 'Gelöscht'}
                </span>
              </div>
              <div className="stack" style={{ gap: '0.5rem' }}>
                <button
                  className="button ghost"
                  style={{ width: '100%', justifyContent: 'center' }}
                  onClick={() => save('draft')}
                  disabled={saving}
                >
                  Entwurf speichern
                </button>
                <button
                  className="button primary"
                  style={{ width: '100%', justifyContent: 'center' }}
                  onClick={() => save('published')}
                  disabled={saving}
                >
                  {form.status === 'published' ? 'Aktualisieren' : 'Veröffentlichen'}
                </button>
              </div>
            </div>

            {/* Document settings card */}
            <div className="card">
              <h4>Dokument</h4>
              <div className="stack" style={{ gap: '0.75rem' }}>
                <label>
                  Typ
                  <select
                    value={form.contentType}
                    onChange={(e) => setForm((c) => ({ ...c, contentType: e.target.value }))}
                    disabled={saving}
                  >
                    <option value="post">Beitrag</option>
                    <option value="page">Seite</option>
                  </select>
                </label>
                <label>
                  Slug / URL
                  <input
                    value={form.slug}
                    onChange={(e) => setForm((c) => ({ ...c, slug: e.target.value, _slugEdited: true }))}
                    placeholder="mein-beitrag"
                    disabled={saving}
                  />
                </label>
                <label>
                  Auszug
                  <textarea
                    value={form.excerpt}
                    onChange={(e) => setForm((c) => ({ ...c, excerpt: e.target.value }))}
                    rows={3}
                    placeholder="Kurze Beschreibung…"
                    disabled={saving}
                  />
                </label>
                <div>
                  <div style={{ fontWeight: 600, color: '#374151', marginBottom: '0.4rem', fontSize: '0.9rem' }}>
                    Beitragsbild
                  </div>
                  {form.featuredImage ? (
                    <div className="stack" style={{ gap: '0.6rem' }}>
                      {(form.featuredImage.url || form.featuredImage.thumbnailUrl) ? (
                        <img
                          src={form.featuredImage.url || form.featuredImage.thumbnailUrl}
                          alt={form.featuredImage.alt || 'Beitragsbild'}
                          style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                        />
                      ) : null}
                      <span className="muted small" style={{ wordBreak: 'break-all', fontSize: '0.75rem' }}>
                        {form.featuredImage.objectKey}
                      </span>
                      <div className="button-row compact">
                        <button type="button" className="button ghost small" onClick={() => setPickerOpen(true)}>Ändern</button>
                        <button type="button" className="button danger small" onClick={() => setForm((c) => ({ ...c, featuredImage: null }))}>✕</button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" className="button secondary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => setPickerOpen(true)}>
                      + Beitragsbild setzen
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Version history card */}
            {versionHistory.length > 0 && (
              <div className="card">
                <h4>Versionen</h4>
                <div className="stack" style={{ gap: '0.5rem' }}>
                  {versionHistory.slice(0, 5).map((v) => (
                    <div key={v.version} className="version-item">
                      <div>
                        <strong>v{v.version}</strong>
                        <span className="muted small"> • {new Date(v.savedAt).toLocaleTimeString()}</span>
                      </div>
                      <button
                        type="button"
                        className="button ghost small"
                        onClick={() => setForm((c) => ({ ...c, title: v.title, body: v.body, excerpt: v.excerpt }))}
                      >
                        Wiederherstellen
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      )}

      <MediaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(item) => setForm((c) => ({
          ...c,
          featuredImage: {
            mediaId: item.mediaId,
            objectKey: item.objectKey,
            alt: item.altText,
            url: item.thumbnailUrl || item.accessUrl,
          },
        }))}
      />
    </>
  );
}
